import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Package, Check, Pencil, X } from "lucide-react";

const CATS = ["modem", "bretelle", "cavi", "connettori", "accessori", "generico"];

export default function MaterialiPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("pz");
  const [category, setCategory] = useState("generico");
  const [unitPrice, setUnitPrice] = useState("");

  // inline edit state: { id: {name, unit, category, unit_price} } or null
  const [editing, setEditing] = useState(null);
  const [fMat, setFMat] = useState("all");
  const [fCat, setFCat] = useState("all");
  const [fText, setFText] = useState("");
  const shown = items.filter((m) => (fMat === "all" || m.id === fMat) && (fCat === "all" || m.category === fCat) && (!fText || m.name.toLowerCase().includes(fText.toLowerCase())));
  const selMat = fMat !== "all" ? items.find((m) => m.id === fMat) : null;

  const load = async () => setItems((await api.get("/materials")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/materials", {
        name,
        unit,
        category,
        unit_price: unitPrice === "" ? 0 : Number(unitPrice) || 0,
      });
      toast.success(t("auto.materiale_aggiunto"));
      setName("");
      setUnitPrice("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await api.delete(`/materials/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startEdit = (m) => setEditing({
    id: m.id,
    name: m.name,
    unit: m.unit,
    category: m.category || "generico",
    unit_price: m.unit_price ?? 0,
  });

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/materials/${editing.id}`, {
        name: editing.name,
        unit: editing.unit,
        category: editing.category,
        unit_price: Number(editing.unit_price) || 0,
      });
      toast.success(t("auto.materiale_aggiornato"));
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.anagrafica")}</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.materials")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("auto.categorie_di_modembretellecavi_ecc_con_prezzo_di_addebito_pe")}</p>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <div className="md:col-span-2"><Label>{t("auto.nome_materiale")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auto.es_modem_vodafone_station")} className="rounded-sm" data-testid="mat-name" /></div>
          <div><Label>{t("auto.unità")}</Label><Input value={unit} onChange={(e) => setUnit(e.target.value)} className="rounded-sm" data-testid="mat-unit" /></div>
          <div>
            <Label>{t("common.category")}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-sm" data-testid="mat-cat"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("auto.prezzo_addebito_eur")}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              placeholder="0.00"
              className="rounded-sm font-mono"
              data-testid="mat-price"
            />
          </div>
          <div className="flex items-end"><Button onClick={create} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="mat-add"><Plus className="mr-2 h-4 w-4" /> {t("auto.aggiungi")}</Button></div>
        </div>
      </div>

      <StockFilter items={items} fMat={fMat} setFMat={setFMat} fCat={fCat} setFCat={setFCat} fText={fText} setFText={setFText} selMat={selMat} count={shown.length} />

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
        {shown.map((m) => {
          const isEditing = editing && editing.id === m.id;
          const price = Number(m.unit_price ?? 0);
          return (
            <div key={m.id} className="rounded-sm border border-slate-200 bg-white p-4" data-testid={`mat-row-${m.id}`}>
              {!isEditing ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Package className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-500" />
                    <div>
                      <div className="font-semibold text-slate-900">{m.name}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span className="font-mono">{m.unit}</span>
                        <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 uppercase tracking-wider">{m.category}</span>
                        <span className="font-mono">€ {price.toFixed(2)}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className={`font-mono font-bold ${(Number(m.stock_qty || 0) <= Number(m.min_threshold || 0) && Number(m.min_threshold || 0) > 0) ? "text-red-600" : "text-slate-900"}`}>
                          📦 {Number(m.stock_qty || 0).toFixed(1)} {m.unit}
                        </span>
                        {Number(m.min_threshold || 0) > 0 && <span className="text-slate-400">/ min {Number(m.min_threshold).toFixed(1)}</span>}
                        <Button size="sm" variant="outline" className="ml-auto h-6 px-2 text-xs" onClick={async () => {
                          const v = window.prompt(`Quantità da CARICARE (${m.unit}):`, "0");
                          if (!v) return;
                          try { await api.post(`/materials/${m.id}/stock-movement`, { delta: Math.abs(Number(v)), reason: "Carico" }); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                        }} data-testid={`mat-stock-in-${m.id}`}>{t("auto.carico_btn")}</Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-red-600" onClick={async () => {
                          const v = window.prompt(`Quantità da SCARICARE (${m.unit}):`, "0");
                          if (!v) return;
                          try { await api.post(`/materials/${m.id}/stock-movement`, { delta: -Math.abs(Number(v)), reason: "Scarico manuale" }); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                        }} data-testid={`mat-stock-out-${m.id}`}>{t("auto.scarico_btn")}</Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={async () => {
                          const v = window.prompt(`Soglia alert (${m.unit}) — 0 per disattivare:`, String(m.min_threshold || 0));
                          if (v === null) return;
                          try { await api.patch(`/materials/${m.id}`, { min_threshold: Number(v) || 0 }); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                        }} data-testid={`mat-threshold-${m.id}`}>{t("auto.soglia_btn")}</Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(m)} data-testid={`mat-edit-${m.id}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(m.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-sm text-sm font-semibold" data-testid={`mat-edit-name-${m.id}`} />
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} className="rounded-sm text-xs" placeholder={t("auto.unità_v2")} />
                    <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                      <SelectTrigger className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editing.unit_price}
                      onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })}
                      className="rounded-sm font-mono text-xs"
                      placeholder="€"
                      data-testid={`mat-edit-price-${m.id}`}
                    />
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="h-4 w-4 text-slate-500" /></Button>
                    <Button size="sm" onClick={saveEdit} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid={`mat-save-${m.id}`}><Check className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full rounded-sm border border-dashed border-slate-300 p-8 text-center text-slate-400">{t("auto.nessun_materiale")}</div>}
        {items.length > 0 && shown.length === 0 && <div className="col-span-full rounded-sm border border-dashed border-slate-300 p-8 text-center text-slate-400" data-testid="mat-filter-empty">Nessun materiale corrisponde al filtro</div>}
      </div>
    </div>
  );
}

function StockFilter({ items, fMat, setFMat, fCat, setFCat, fText, setFText, selMat, count }) {
  const low = selMat && Number(selMat.min_threshold || 0) > 0 && Number(selMat.stock_qty || 0) <= Number(selMat.min_threshold || 0);
  const reset = () => { setFMat("all"); setFCat("all"); setFText(""); };
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-4" data-testid="mat-filter">
      <div className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Filtra giacenza</div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
        <div className="md:col-span-4">
          <Label>Materiale</Label>
          <Select value={fMat} onValueChange={setFMat}>
            <SelectTrigger className="rounded-sm" data-testid="mat-filter-material"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i materiali</SelectItem>
              {items.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Label>Categoria</Label>
          <Select value={fCat} onValueChange={setFCat}>
            <SelectTrigger className="rounded-sm" data-testid="mat-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Label>Cerca</Label>
          <Input value={fText} onChange={(e) => setFText(e.target.value)} placeholder="es. fibra interno" className="rounded-sm" data-testid="mat-filter-search" />
        </div>
        <div className="flex items-end gap-2 md:col-span-2">
          <Button variant="outline" className="w-full rounded-sm" onClick={reset} data-testid="mat-filter-reset">Azzera</Button>
        </div>
      </div>
      {selMat ? (
        <div className={`mt-4 flex flex-wrap items-baseline gap-3 rounded-sm border p-4 ${low ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="mat-filter-stock">
          <span className="text-sm font-semibold text-slate-700">Giacenza {selMat.name}:</span>
          <span className={`font-mono text-3xl font-bold ${low ? "text-red-600" : "text-emerald-700"}`}>{Number(selMat.stock_qty || 0).toFixed(1)} {selMat.unit}</span>
          {Number(selMat.min_threshold || 0) > 0 && <span className="text-xs text-slate-500">soglia minima {Number(selMat.min_threshold).toFixed(1)} {selMat.unit}{low ? " — da riordinare" : ""}</span>}
        </div>
      ) : (
        <div className="mt-3 text-xs text-slate-500" data-testid="mat-filter-count">{count} materiali visualizzati</div>
      )}
    </div>
  );
}
